import * as Yup from 'yup';
import { ApprovalRuleDetail, ruleStatus } from '../inventoryApproval.types';
import {
  ApprovalLevelFormValues,
  CreateApprovalRuleFormValues,
  CreateApprovalRulePayload,
  RolesResponse,
} from './createApprovalRule.types';
import { getRequest, postRequest, putRequest } from '../../../../services/httpService';
// import { getRequest, postRequest, putRequest } from '../../../../services/httpOldService';

// ─── Empty approval level template ───
export const getEmptyApprovalLevel = (levelNumber: number): ApprovalLevelFormValues => ({
  level: levelNumber,
  role: null,
  escalationTime: '',
  escalationRole: null,
  isMandatory: false,
  skipIfSameUser: false,
});

// ─── Validation schema ───
export const createApprovalRuleValidationSchema = (t: (key: string) => string) =>
  Yup.object().shape({
    ruleName: Yup.string().trim().required(t('val_rule_name_required')),
    documentType: Yup.number().nullable().required(t('val_document_type_required')),
    clinicId: Yup.number().required(t('val_clinic_required')),
    status: Yup.string().required(t('val_status_required')),
    minAmount: Yup.number()
      .typeError(t('val_must_be_number'))
      .min(0, t('val_cannot_be_negative'))
      .required(t('val_min_amount_required')),
    maxAmount: Yup.number()
      .typeError(t('val_must_be_number'))
      .when('isUnlimited', {
        is: false,
        then: (schema) =>
          schema
            .required(t('val_max_amount_required'))
            .min(Yup.ref('minAmount'), t('val_max_gte_min')),
        otherwise: (schema) => schema.notRequired(),
      }),
    isUnlimited: Yup.boolean(),
    approvalLevels: Yup.array()
      .of(
        Yup.object().shape({
          role: Yup.number()
            .nullable()
            .required(t('val_role_required'))
            .test(
              'not-same-role',
              t('val_role_differ_escalation'),
              function (role) {
                return this.parent.escalationRole !== role;
              },
            )
            .test(
              'unique-role-across-levels',
              t('val_role_unique'),
              function (role) {
                if (role == null) return true;
                const allLevels = (this.from?.[1]?.value?.approvalLevels ?? []) as ApprovalLevelFormValues[];
                const count = allLevels.filter((lvl) => lvl.role === role).length;
                return count <= 1;
              },
            ),
          escalationTime: Yup.string(),
          escalationRole: Yup.number()
            .nullable()
            .test(
              'not-same-role',
              t('val_escalation_differ_role'),
              function (escalationRole) {
                if (escalationRole == null) return true;
                return escalationRole !== this.parent.role;
              },
            ),
        }),
      )
      .min(1, t('val_one_level_required')),
    notifyEmail: Yup.boolean(),
    notifySystem: Yup.boolean(),
  });

// ─── Map detail API response → form values for edit mode ───
export const mapApprovalRuleToFormValues = (
  rule: ApprovalRuleDetail,
): CreateApprovalRuleFormValues => ({
  ruleName: rule.name,
  documentType: rule.documentType.id,
  clinicId: rule.clinic,
  status: rule.status,
  minAmount: Number(rule.min_amount),
  maxAmount: rule.max_amount !== null ? Number(rule.max_amount) : null,
  isUnlimited: rule.max_amount === null,
  approvalLevels: rule.levels.map((lvl) => ({
    id: lvl.id,
    level: lvl.level_number,
    role: lvl.role,
    escalationTime: lvl.escalation_time_hours != null
      ? `${String(lvl.escalation_time_hours).padStart(2, '0')}:${String(lvl.escalation_time_minutes ?? 0).padStart(2, '0')}`
      : '',
    escalationRole: lvl.escalation_role,
    isMandatory: lvl.is_mandatory,
    skipIfSameUser: lvl.skip_if_same_user,
  })),
  notifyEmail: rule.is_email,
  notifySystem: rule.is_system,
});


//get all roles
export function getRoles(): Promise<RolesResponse> {
  const url = '/roles/';
  return getRequest(url, { responseType: 'json' });
}


export function createApprovalRule(data: CreateApprovalRulePayload): Promise<CreateApprovalRulePayload> {
  const url = 'approval-rules/';
  return postRequest(url, data, { responseType: 'json' });
}

export function updateApprovalRule(id: number, data: CreateApprovalRulePayload): Promise<CreateApprovalRulePayload> {
  const url = `approval-rules/${id}/`;
  return putRequest(url, data, { responseType: 'json' });
}

// ─── Map form values → API payload ───
export function mapFormValuesToPayload(
  values: CreateApprovalRuleFormValues,
): CreateApprovalRulePayload {
  return {
    name: values.ruleName,
    document_type: values.documentType!,
    clinic: values.clinicId!,
    min_amount: String(values.minAmount ?? 0),
    max_amount: values.isUnlimited || values.maxAmount == null ? null : String(values.maxAmount),
    status: values.status.toUpperCase() as ruleStatus,
    priority: 1,
    is_email: values.notifyEmail,
    is_system: values.notifySystem,
    is_mandatory: values.approvalLevels.some((lvl) => lvl.isMandatory),
    levels: values.approvalLevels.map((lvl) => ({
      level_number: lvl.level,
      role: lvl.role!,
      escalation_role: lvl.escalationRole ?? null,
      escalation_time_hours: lvl.escalationTime
        ? Number(lvl.escalationTime.split(':')[0])
        : null,
      escalation_time_minutes: lvl.escalationTime
        ? Number(lvl.escalationTime.split(':')[1])
        : null,
      is_mandatory: lvl.isMandatory,
      skip_if_same_user: lvl.skipIfSameUser,
    })),
  };
}